/**
 * insuranceCommit.ts — what a year of cover's save is allowed to be, and the order it happens in.
 *
 * REFUSE → CHECK THE DOCUMENT → UPLOAD → POLICY → JUNCTION → IDENTITY → THE MONEY, and every arrow
 * is load-bearing.
 *
 * REFUSE FIRST, WHICH IS `moneyCommit`'s RULE APPLIED AGAIN. These are unwrapped round trips with no
 * transaction around them, so a refusal that arrives after `insurances` has been written leaves a
 * policy the admin did not mean to create — and a policy that exists is a row on the Insurance tab,
 * a junction the setup canvas grades and a premium the Unassigned queue has stopped counting.
 *
 * THE CERTIFICATE IS CHECKED BEFORE A BYTE IS UPLOADED, and the upload is checked before the policy
 * row exists. A refused certificate that had already been stored leaves an orphaned object nothing
 * references; a policy row whose junction insert then fails is cover no contract is on, which is
 * worse — so those two are the ONLY steps with rollback arms, and they undo exactly what they wrote.
 *
 * AND NOTHING AFTER THE JUNCTION IS ROLLED BACK, deliberately. A loan without its instalment is a
 * real arrangement — a premium settled at the end of the contract, which the side sheet this
 * replaces allowed — so undoing a loan because its weekly recovery failed would delete a debt over a
 * transport error. The half-written state is visible: every one of those rows has a line on the rail
 * and the area's own bar saves it again. What the sheet did instead was retry the whole nine-call
 * sequence from the top, which wrote a SECOND policy and a second junction every time.
 *
 * `onIdentity` FIRES BETWEEN THE JUNCTION AND THE PREMIUM, WHICH IS THE WHOLE RESUME-AS-AN-EDIT
 * RULE. The moment the junction lands the subject exists and the URL can name it; everything after
 * that point is an ordinary area save on a policy that is already there. So the page is told there
 * and then — it drops the staged certificate from its edits, marks the certificate row committed and
 * replaces the route — and a failure of the premium statement below leaves an admin on a real policy
 * with a bar that saves the rest, rather than on a create screen whose second attempt would insert
 * everything twice.
 *
 * THE PLANNER IS SEPARATE FROM THE CALLS, AND THAT IS THE WHOLE TESTABILITY OF THIS FILE. The ORDER
 * of the statements is the safety property; the four planners return it as data, so a test pins the
 * sequence, both rollback arms and every adopt without a network.
 *
 * NO RPC IN ANY SAVE PATH. `delete_insurance_cascade` at the foot of this file is the only one, and
 * it is a delete.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

import { AGREEMENT_BOARD_SUBJECTS_KEY } from '@/components/agreements/boardSubjects';
import { DEPOSIT_LEDGER_KEY } from '@/components/insurance-sub/depositLedger';
import { PREMIUM_YEARS_KEY } from '@/components/insurance-sub/premiumLedger';
import {
    ATTEST_PROFILE_UNKNOWN,
    certificateAccepted,
    NOT_PDF_REFUSAL_PREFIX,
    NOT_STATUTORY_REFUSAL_PREFIX,
    NO_IDENTIFIER_REFUSAL_PREFIX,
    TOO_LARGE_REFUSAL_PREFIX,
    UNREADABLE_REFUSAL_PREFIX,
    VEHICLE_UNRECORDED_REFUSAL_PREFIX,
} from '@/lib/certificatePrefill';
import { readCertificateCoverPeriod } from '@/lib/certificateRead';
import { VEHICLE_REFUSAL_PREFIX, type ContractVehicle } from '@/lib/certificateVehicle';
import type { Database, TablesInsert } from '@/integrations/supabase/types';

import { advanceForwardKey } from '../advance/useAdvanceState';
import { CONTRACT_INSURANCE_KEY, SERVER_NOW } from '../data/insurance';
import {
    attributeRefusal,
    type RefusalFieldMap,
    type RefusalPrefixMap,
} from '../data/refusalFields';
import { contractPolicyChainKey } from './data/insurancePolicy';
import { depositsCoverPremium } from './insuranceArithmetic';
import type { InsuranceDraft, InsuranceExistingIds, InsuranceField } from './insuranceForm';
import type { HpClass, InsuranceRowId } from './insuranceState';
import type { InsuranceArea } from './insuranceSteps';

/** The client, structurally — injected so this module never imports the browser singleton. */
export type InsuranceClient = SupabaseClient<Database>;

export type InsuranceMode = 'create' | 'edit';

/**
 * The create's draft, with the contract it is opened on and the vehicle it insures.
 *
 * THE VEHICLE IS THE CERTIFICATE'S BUSINESS AND NOT THE POLICY'S. `insurances.vehicle_id` is what
 * the row is written against; the plate and chassis are what a staged Certificate of Insurance is
 * checked against, and the guard carries both rather than reaching for the second from the first.
 */
export interface InsuranceCreate extends InsuranceDraft {
    contractId: number;
    vehicleId: number;
    vehicle: ContractVehicle;
    /**
     * What the contract's hire purchase agreement is [D-B57] — the fact that decides whether this
     * premium may be financed without a weekly recovery.
     *
     * IT RIDES ON THE CREATE'S OWN DRAFT because the create has no save context to carry it: the
     * junction does not exist yet, so there is nothing for a context to be about.
     */
    hpClass: HpClass;
}

/**
 * What an edit is written against: the two ids that own the rows, and the rows the junction has.
 *
 * `loanId` IS NULL ON 54 LIVE JUNCTIONS, which is why every area save below is an
 * insert-or-adopt/update rather than an update: a year that recorded cover and financed nothing is
 * a year whose first advance save has to CREATE the loan.
 */
export interface InsurancePolicySubject extends InsuranceExistingIds {
    /** `insurances.id` — the policy this contract is on, and what the premium hangs off. */
    policyId: number;
    /** `insurance_contracts.id` — what a loan hangs off. NEVER the policy id. */
    junctionId: number;
}

export interface InsuranceRefusal {
    /** The FORM's field name, so the bar, the attribution maps and the canvas all agree. */
    field: InsuranceField;
    /** The area holding the field, so a bar can say where to go — and scope itself to its own. */
    area: InsuranceArea;
    /** The rail row this lands on, where it has one. */
    rowId: InsuranceRowId | null;
    message: string;
}

/**
 * THE SENTENCES THE BARS SPEAK, and they carry no decision id: an admin reading a refusal is being
 * told what to do, not which ruling it came from. The ids stay in the docblocks.
 */
const REFUSALS = {
    valuedAt: 'Enter what the vehicle is insured for.',
    /** `insurances.valued_at` is nullable, and 50 live policies hold NULL in it. */
    valuedAtCleared: 'The insured value on file can be corrected, not cleared.',
    startsAt: 'Choose the day the cover starts.',
    expiresAt: 'Choose the day the cover expires.',
    /** The dossier's own rule, and the one refusal the setup canvas deliberately does not carry. */
    periodOrder: 'The cover cannot expire before it starts.',
    premium: 'Select the payment made to the insurer.',
    premiumCleared:
        'A premium payment on file can be corrected, not cleared — recording it is what keeps the ' +
        'transaction out of the Unassigned queue.',
    /** `insurance_premium_discounts.premium_id` is nullable, so nothing below this refuses it. */
    discountOrphan:
        'Select the premium payment first — a discount is recorded against the premium it came ' +
        'back from.',
    amount: 'Enter the premium for the insurance period.',
    amountCleared: 'The premium on file can be corrected, not cleared.',
    positive: 'The weekly recovery has to be more than zero.',
    half: 'A weekly recovery needs both the amount and the day it starts.',
    /** [D-B57] — a financed premium comes back weekly wherever the agreement leaves room for it. */
    weeklyRequired: 'Enter the weekly recovery. A premium financed on this contract is recovered weekly.',
    firstRequired: 'Choose the day the first recovery falls due.',
    /** Correcting a live recovery is an edit; removing it is not what this bar is for. */
    weeklyCleared: 'A weekly recovery already running can be corrected, not cleared.',
    /** `UNIQUE (transaction_id)` — one debit records one premium. */
    premiumTaken: 'That payment is already recorded against another policy.',
    discountTaken: 'That credit is already recorded against another policy.',
    depositTaken: 'That payment is already recorded against another policy.',
    /** `UNIQUE (loan_id, starts_at)` — one weekly figure effective from any one day. */
    weekTaken: 'That week already carries a recovery on this advance.',
} as const;

function filled(value: string | null | undefined): boolean {
    return !!value && value.trim().length > 0;
}

function given(value: number | null | undefined): boolean {
    return value !== null && value !== undefined;
}

/**
 * WHAT THE WRITER HAS TO BE TOLD ABOUT THE YEAR BEHIND THE DRAFT [D-B57].
 *
 * NEITHER IS A FACT THIS MODULE COULD READ FOR ITSELF, and that is why they are arguments. The
 * class of the hire purchase agreement is derived from the agreements board's slots; the other
 * deposits are the rows the dossier does not bind. A writer that fetched either would be a second
 * opinion about a contract the page has already answered.
 */
export interface InsuranceContractFacts {
    /** The contract's hire purchase agreement, as the board answered it. */
    hpClass: HpClass;
    /** Σ of the deposit rows this screen does not edit [D-B45] — the Record limb turns on it. */
    otherDeposits: number;
}

/**
 * Would this save publish an Insurance Premium Payment Record? — [D-B45]'s two limbs, asked of the
 * DRAFT.
 *
 * DERIVED HERE AND NOT HANDED DOWN, which is the whole point of it being in the writer: the page,
 * the create and the three area saves would otherwise each spell the same two limbs, and a
 * required-weekly rule fired over a Record would ask for a figure this very save is about to drop.
 */
function publishesPaymentRecord(draft: InsuranceDraft, otherDeposits: number): boolean {
    const writesPair = given(draft.weeklyAmount) && filled(draft.firstStartsAt);
    return !writesPair && draftCoversPremium(draft, otherDeposits);
}

/**
 * Every reason this draft cannot be saved, in reading order.
 *
 * ALL OF THEM, NOT THE FIRST — the create bar names one and counts the rest, and an admin who fixes
 * the named one should not discover a second refusal they could have fixed in the same pass. An
 * area bar reads the refusals whose `area` it owns.
 *
 * THE CERTIFICATE IS NOT IN THIS LIST, AND CANNOT BE. Its refusal is a property of the DOCUMENT and
 * of a tick box beside it — the shared certificate row holds both, and hands the bar either the
 * rejection it is already showing or the offer standing un-ticked. A draft carrying a `File` says
 * nothing about whether the bytes read, and this module is handed a draft.
 *
 * THE WEEKLY PAIR CLEARED OVER A SAVED ROW IS A DELETE ON A CONTRACT WHOSE HIRE PURCHASE AGREEMENT
 * WAS SIGNED ON PAPER, AND A REFUSAL ON EVERY OTHER ONE — which is where this writer parts company
 * with the advance's [D-B54], and since [D-B57] only there. An operational advance's schedule may
 * be corrected and not cleared because every advance is repaid weekly; a premium financed on a
 * paper agreement may be settled at the end of the contract instead, the side sheet wrote exactly
 * that, and the deposit rule below [D-B45] clears a typed pair on purpose. So on those the
 * else-arm deletes the row and the canvas says out loud that it is doing so. Where the agreement
 * was signed in the app there is room for an advance instalment, the weekly recovery is required,
 * and an empty pair over a financed premium is refused rather than written.
 *
 * THE FOURTH ARGUMENT IS WHAT MAKES THAT ASKABLE, and it is required rather than defaulted for the
 * reason `InsuranceSaveContext.otherDeposits` gives: the default is the unsafe direction. A caller
 * that had not worked out what the contract is has not worked out what it is allowed to write.
 */
export function validateDraft(
    draft: InsuranceDraft,
    mode: InsuranceMode,
    existing: InsuranceExistingIds | null,
    facts: InsuranceContractFacts,
): InsuranceRefusal[] {
    const refusals: InsuranceRefusal[] = [];
    const push = (
        field: InsuranceField,
        area: InsuranceArea,
        rowId: InsuranceRowId | null,
        message: string,
    ) => refusals.push({ field, area, rowId, message });

    // ── Policy ───────────────────────────────────────────────────────────────
    if (given(draft.valuedAt) && (draft.valuedAt as number) <= 0) {
        // A TYPED NOUGHT IS REFUSED WHEREVER IT APPEARS, as the premium's is: a vehicle insured for
        // nothing is not a record of anything, and the box is `given` so the save would write it.
        push('valuedAt', 'policy', 'pol-value', REFUSALS.valuedAt);
    } else if (!given(draft.valuedAt)) {
        /*
         * AN ABSENCE IS THE PREMIUM AMOUNT'S QUESTION AND GETS THE PREMIUM AMOUNT'S ANSWER.
         * `insurances.valued_at` is nullable and 50 live policies hold NULL in it, every one on a
         * loan-less junction — so a refusal that stood unconditionally would disable every bar on
         * the page for 50 of the 54 junctions this dossier exists to edit, over a column none of
         * them has ever carried. A create states it; an edit is refused only where the row already
         * holds a value this draft has emptied, which is the one case that DESTROYS a record.
         */
        if (mode === 'create') {
            push('valuedAt', 'policy', 'pol-value', REFUSALS.valuedAt);
        } else if (given(existing?.valuedAt)) {
            push('valuedAt', 'policy', 'pol-value', REFUSALS.valuedAtCleared);
        }
    }
    if (!filled(draft.startsAt)) push('startsAt', 'policy', 'pol-period', REFUSALS.startsAt);
    if (!filled(draft.expiresAt)) push('expiresAt', 'policy', 'pol-period', REFUSALS.expiresAt);
    /*
     * BOTH ENDS ARE `'yyyy-MM-dd'`, SO A STRING COMPARISON IS THE CHRONOLOGICAL ONE — no parse, no
     * zone, and no host clock moving a boundary. The rule is refused on an edit too: a period that
     * ends before it begins is a contradiction the instruments print, and this dossier is
     * authoritative for the policies it writes.
     */
    if (
        filled(draft.startsAt) &&
        filled(draft.expiresAt) &&
        (draft.expiresAt as string) < (draft.startsAt as string)
    ) {
        push('expiresAt', 'policy', 'pol-period', REFUSALS.periodOrder);
    }

    // ── Premium ──────────────────────────────────────────────────────────────
    if (!given(draft.premiumTransactionId)) {
        // ON AN EDIT IT IS ONLY A REFUSAL WHERE ONE IS ON FILE. A junction whose premium has never
        // been recorded stays saveable from its other areas; one that HAS a premium is not
        // un-recorded from here, because that is what puts a transaction back in the queue.
        if (mode === 'create') {
            push('premiumTransactionId', 'premium', 'pol-premium', REFUSALS.premium);
        } else if (existing?.premiumId !== null && existing?.premiumId !== undefined) {
            push('premiumTransactionId', 'premium', 'pol-premium', REFUSALS.premiumCleared);
        }
    }

    /*
     * A DISCOUNT HANGS OFF A PREMIUM, AND THE COLUMN THAT SAYS SO IS NULLABLE.
     * `insurance_premium_discounts.premium_id` carries no NOT NULL, so a credit linked on a junction
     * whose premium has never been recorded would insert a row pointing at nothing: unreachable from
     * every screen — the policy read reaches discounts only through `insurance_premiums` — counted
     * in the estate's discount totals, and, worst of all, silently out of the Unassigned queue,
     * which excludes any transaction this table names. So the link is refused until there is a
     * premium for it to hang off, whether one is on file or this same save is about to write it.
     */
    if (
        given(draft.discountTransactionId) &&
        !given(draft.premiumTransactionId) &&
        !given(existing?.premiumId)
    ) {
        push('discountTransactionId', 'premium', 'pol-discount', REFUSALS.discountOrphan);
    }

    // ── Advance ──────────────────────────────────────────────────────────────
    const hasWeekly = given(draft.weeklyAmount);
    const hasDate = filled(draft.firstStartsAt);
    const touchesAdvance =
        hasWeekly || hasDate || given(draft.depositTransactionId) || given(draft.interestRate);

    if (given(draft.premiumAmount) && (draft.premiumAmount as number) <= 0) {
        /*
         * A TYPED NOUGHT IS REFUSED WHEREVER IT APPEARS, AND THAT IS NOT THE SAME QUESTION AS AN
         * EMPTY BOX. `advanceStatements` reads `0` as a premium to write — it is `given` — so on one
         * of the 54 loan-less junctions a lone `0` in the premium box, with nothing else on this
         * canvas touched, planned an `insurance_loans` row with `amount: 0`: a debt of nothing that
         * no screen here can remove (contract setup's own delete is the broken door), and which
         * takes the agreement rows out of dormancy so an instrument becomes generatable over a year
         * that financed nothing. The conditions below are about ABSENCE and cannot speak to it.
         */
        push('premiumAmount', 'advance', 'pol-amount', REFUSALS.amount);
    } else if (!given(draft.premiumAmount)) {
        // `insurance_loans.amount` IS NOT NULL: a loan states what the premium was, and every other
        // figure on the canvas is about that one. On an edit it is asked for only where the year
        // already carries a loan, or where something else on this canvas would write one.
        if (mode === 'create') {
            push('premiumAmount', 'advance', 'pol-amount', REFUSALS.amount);
        } else if (given(existing?.loanId)) {
            push('premiumAmount', 'advance', 'pol-amount', REFUSALS.amountCleared);
        } else if (touchesAdvance) {
            push('premiumAmount', 'advance', 'pol-amount', REFUSALS.amount);
        }
    }

    if (hasWeekly && (draft.weeklyAmount as number) <= 0) {
        // THE SIDE SHEET WROTE A ZERO WEEKLY, and a recovery of nought is a loan that claims a
        // schedule and bills nothing.
        push('weeklyAmount', 'advance', 'pol-weekly', REFUSALS.positive);
    }

    if (hasWeekly !== hasDate) {
        push(
            hasWeekly ? 'firstStartsAt' : 'weeklyAmount',
            'advance',
            hasWeekly ? 'pol-first' : 'pol-weekly',
            REFUSALS.half,
        );
    }

    /*
     * AND AN EMPTY PAIR IS REFUSED WHERE THE HIRE PURCHASE AGREEMENT LEAVES ROOM FOR ONE [D-B57].
     * A premium financed on a contract whose agreement was signed on paper is settled at the end of
     * the contract; every other contract recovers a financed premium weekly, and an absent pair
     * there is an omission rather than an arrangement.
     *
     * THE PRECEDENCE IS EXEMPT → OPTIONAL → REQUIRED. A save whose deposits cover the premium
     * publishes a Payment Record, which advances nothing and recovers nothing, so it is exempt
     * before the class is even asked. `'PAPER'` and `'UNREAD'` are the optional arm: a board that
     * came back refused has established nothing about the contract, and refusing every save on the
     * strength of a failed request would strand the year — while the server, which picks the
     * instrument itself, is the backstop that a courtesy refusal here never was.
     *
     * AND A LOAN-LESS JUNCTION IS NEVER ASKED. `advanceStatements` writes nothing at all where the
     * draft carries no premium and the junction no loan — 54 live junctions are that shape — so the
     * predicate below is that same one, and a year financing nothing is not asked how it comes back.
     */
    const financed = given(draft.premiumAmount) || given(existing?.loanId);
    const weeklyRequired =
        !hasWeekly &&
        !hasDate &&
        facts.hpClass === 'NOT_PAPER' &&
        financed &&
        !publishesPaymentRecord(draft, facts.otherDeposits);

    if (weeklyRequired) {
        if (mode === 'edit' && given(existing?.instalmentId)) {
            // A LIVE RECOVERY IS CORRECTED AND NOT CLEARED. Removing it moves `weekly_rate` — one
            // of the five protected numbers — as a side effect of emptying two boxes, so the door
            // that D-B45 leaves open on a paper agreement is shut here.
            push('weeklyAmount', 'advance', 'pol-weekly', REFUSALS.weeklyCleared);
        } else {
            push('weeklyAmount', 'advance', 'pol-weekly', REFUSALS.weeklyRequired);
            push('firstStartsAt', 'advance', 'pol-first', REFUSALS.firstRequired);
        }
    }

    return refusals;
}

// ─── The statements, as data ─────────────────────────────────────────────────

/**
 * One write, named rather than made.
 *
 * `identity` IS NOT A WRITE AT ALL, and it is in this list because its POSITION is the property
 * worth pinning: it is the moment the subject exists, and everything after it is an edit.
 */
export type InsuranceStatement =
    | { op: 'file-certificate' }
    | { op: 'insert-policy' }
    | { op: 'update-policy'; policyId: number }
    | { op: 'insert-junction' }
    | { op: 'identity' }
    | { op: 'insert-premium' }
    | { op: 'update-premium'; premiumId: number }
    | { op: 'insert-discount' }
    | { op: 'update-discount'; discountId: number }
    | { op: 'delete-discount'; discountId: number }
    | { op: 'insert-loan' }
    | { op: 'update-loan'; loanId: number }
    | { op: 'insert-deposit' }
    | { op: 'update-deposit'; depositId: number }
    | { op: 'delete-deposit'; depositId: number }
    | { op: 'insert-instalment' }
    | { op: 'update-instalment'; instalmentId: number }
    | { op: 'delete-instalment'; instalmentId: number };

/** The certificate is filed only where this save staged one — which is most saves' answer of no. */
function certificateStatements(draft: InsuranceDraft): InsuranceStatement[] {
    return draft.certificateFile ? [{ op: 'file-certificate' }] : [];
}

/**
 * The premium and its discount.
 *
 * THE DISCOUNT IS DELETED WHERE IT IS CLEARED, which is the sheet's own else-arm: a credit that was
 * linked and is now not is a link to remove, not a row to leave behind pointing at a transaction
 * this year no longer claims.
 *
 * AND THE TWO WRITE ARMS ARE GATED ON THERE BEING A PREMIUM, WHILE THE DELETE ARM IS NOT. The
 * `premium_id` a discount is written with is whatever this sequence has resolved by then — the row
 * on file, or the one the statement above is about to insert — and where there is neither it would
 * be NULL, which `validateDraft` now refuses for the reasons stated there. The gate is the second
 * lock rather than the first: a refusal is what an admin reads, and this is what makes the write
 * impossible to reach even if some later caller skips the refusals. Removing a link needs no
 * premium, and a junction whose premium row was deleted underneath a standing discount is exactly
 * when that arm has to run.
 */
function premiumStatements(
    draft: InsuranceDraft,
    existing: InsuranceExistingIds | null,
): InsuranceStatement[] {
    const statements: InsuranceStatement[] = [];
    const premiumId = existing?.premiumId ?? null;
    const discountId = existing?.discountId ?? null;
    const willHavePremium = premiumId !== null || given(draft.premiumTransactionId);

    if (given(draft.premiumTransactionId)) {
        statements.push(
            premiumId === null ? { op: 'insert-premium' } : { op: 'update-premium', premiumId },
        );
    }

    if (given(draft.discountTransactionId)) {
        if (willHavePremium) {
            statements.push(
                discountId === null
                    ? { op: 'insert-discount' }
                    : { op: 'update-discount', discountId },
            );
        }
    } else if (discountId !== null) {
        statements.push({ op: 'delete-discount', discountId });
    }

    return statements;
}

/**
 * The loan, the driver's own payment and the weekly recovery — ONE commit, because the three
 * decide each other [D-B45].
 *
 * THE LOAN IS WRITTEN WHEREVER THERE IS A PREMIUM TO WRITE, and adopted where the junction already
 * carries one: `UNIQUE (insurance_contract_id)` refuses a second insert, and that refusal is
 * precisely the news that another save created the row this one intended to. Two loans on one
 * junction is what `weekly_rate` adds up while `total_value` reads only the first — it happened, on
 * contract #318.
 *
 * THE DEPOSIT IS A LINK AND NEVER THE TRANSACTION. Clearing it removes the link; the money stays
 * exactly where it is in the ledger.
 *
 * AND THE INSTALMENT IS DELETED WHERE THE PAIR IS CLEARED OVER A SAVED ROW — the sheet's parity,
 * and the one thing on this canvas that removes a live weekly recovery. `validateDraft` lets it
 * through on purpose; the canvas says so beside the fields.
 */
function advanceStatements(
    draft: InsuranceDraft,
    existing: InsuranceExistingIds | null,
): InsuranceStatement[] {
    const statements: InsuranceStatement[] = [];
    const loanId = existing?.loanId ?? null;
    const depositId = existing?.depositId ?? null;
    const instalmentId = existing?.instalmentId ?? null;

    if (!given(draft.premiumAmount) && loanId === null) return statements;

    statements.push(loanId === null ? { op: 'insert-loan' } : { op: 'update-loan', loanId });

    if (given(draft.depositTransactionId)) {
        statements.push(
            depositId === null ? { op: 'insert-deposit' } : { op: 'update-deposit', depositId },
        );
    } else if (depositId !== null) {
        statements.push({ op: 'delete-deposit', depositId });
    }

    const writesInstalment = given(draft.weeklyAmount) && filled(draft.firstStartsAt);
    if (writesInstalment) {
        statements.push(
            instalmentId === null
                ? { op: 'insert-instalment' }
                : { op: 'update-instalment', instalmentId },
        );
    } else if (instalmentId !== null) {
        statements.push({ op: 'delete-instalment', instalmentId });
    }

    return statements;
}

/**
 * The whole create, in the order ruling 4 states it.
 *
 * THE POLICY IS AN INSERT AND SO IS THE JUNCTION, with NO `23505` adopt on either: an adopt says
 * "another save already made the row I meant to make", which is true of a loan on a junction this
 * page is looking at and false of a policy nobody has an id for yet. Adopting there would attach
 * this contract to somebody else's cover.
 */
export function planCreate(draft: InsuranceDraft): InsuranceStatement[] {
    return [
        ...certificateStatements(draft),
        { op: 'insert-policy' },
        { op: 'insert-junction' },
        { op: 'identity' },
        ...premiumStatements(draft, null),
        ...advanceStatements(draft, null),
    ];
}

/** The policy area: the three values, and the certificate where one was staged. */
export function planPolicyArea(
    draft: InsuranceDraft,
    subject: InsurancePolicySubject,
): InsuranceStatement[] {
    return [...certificateStatements(draft), { op: 'update-policy', policyId: subject.policyId }];
}

export function planPremiumArea(
    draft: InsuranceDraft,
    subject: InsurancePolicySubject,
): InsuranceStatement[] {
    return premiumStatements(draft, subject);
}

export function planAdvanceArea(
    draft: InsuranceDraft,
    subject: InsurancePolicySubject,
): InsuranceStatement[] {
    return advanceStatements(draft, subject);
}

// ─── The executor ────────────────────────────────────────────────────────────

/** `23505` — a UNIQUE index refused the row. Which one it was is the statement's own business. */
const DUPLICATE = '23505';

/** The bucket every Certificate of Insurance this app holds is stored in. */
const CERTIFICATE_BUCKET = 'insurances';

/** What the upload left behind, and the two columns that go down with it. */
interface FiledCertificate {
    url: string;
    /** The object's path in the bucket — what the rollback arms remove. */
    path: string;
    attestedAt: string | null;
    attestedBy: number | null;
}

/** The identity of a policy the moment it exists. */
export interface InsuranceIdentity {
    policyId: number;
    junctionId: number;
}

/**
 * A POLICY IS WRITTEN OVER A VEHICLE, AND SOME CONTRACTS HAVE NONE.
 *
 * `insurances.vehicle_id` is NOT NULL, so a create is impossible; and a save that files a
 * certificate checks the document against this contract's plate and chassis, which a contract with
 * no vehicle does not have. It lives HERE, beside the two statements that open the car, so the bar
 * that prints the sentence before the click and the writer that raises it after are quoting one
 * string.
 */
export const NO_VEHICLE_REFUSAL =
    'This contract has no vehicle on it, and a policy is written over one. Add the vehicle first.';

interface WriteContext {
    contractId: number;
    /**
     * NULL WHERE THE PLAN CANNOT OPEN IT — see `InsuranceSaveContext`. Exactly two statements read
     * the car, `insert-policy` and `file-certificate`, and neither is ever planned by the premium
     * or the advance area; both guard rather than assert, because a nullable field whose readers
     * shrug is how a NOT NULL column ends up sent an undefined.
     */
    vehicleId: number | null;
    vehicle: ContractVehicle | null;
    draft: InsuranceDraft;
    /** Σ of the deposit rows this screen does not edit — nought on a junction being created. */
    otherDeposits: number;
    /** This year's instrument carries a signature: the columns it printed are reads [F7]. */
    frozen: boolean;
    policyId: number | null;
    junctionId: number | null;
    premiumId: number | null;
    loanId: number | null;
    certificate: FiledCertificate | null;
    onIdentity?: (identity: InsuranceIdentity) => void;
}

/**
 * WHICH `profiles` ROW THIS SESSION IS, for the one column that records a person.
 *
 * IT IS THE INJECTED CLIENT'S LOOKUP AND NOT `attestingProfileId`'s, which is the same two round
 * trips against the browser singleton. This module never touches that singleton — that is what
 * makes every statement below provable without a network — and the sentence a failure raises is
 * imported rather than restated, because the attribution maps match on it exactly.
 *
 * AN ATTESTATION WITH NO AUTHOR IS NOT THE RECORD [D-B56] ASKED FOR: written as NULL, the standing
 * line reads `Filed on an admin's reading…` for ever and no later reader can find out whose. So the
 * save stops before the upload.
 */
async function attestingProfileId(client: InsuranceClient): Promise<number> {
    let id: number | null = null;
    try {
        const { data: auth } = await client.auth.getUser();
        if (auth?.user) {
            const { data } = await client
                .from('profiles')
                .select('id')
                .eq('user_id', auth.user.id)
                .maybeSingle();
            id = data?.id ?? null;
        }
    } catch {
        id = null;
    }
    if (id === null) throw new Error(ATTEST_PROFILE_UNKNOWN);
    return id;
}

/**
 * Check the staged certificate and file it.
 *
 * THE DOCUMENT IS READ AGAIN HERE rather than being handed a verdict the screen computed: a
 * disabled control is a courtesy and a piece of component state is not evidence, so the only thing
 * this can be sure of is the bytes it is about to upload. It runs BEFORE the upload, so a refused
 * certificate never reaches storage and never leaves an orphaned object behind.
 *
 * `certificateAccepted` IS THE SINGLE AUTHOR OF THE DECISION for both save doors, this one and the
 * setup canvas's — including the one door through it [D-B56], where a scan nobody can read is filed
 * on the admin's own reading of it and only where THAT read comes back unreadable again.
 */
async function fileCertificate(
    client: InsuranceClient,
    context: WriteContext,
): Promise<FiledCertificate> {
    const file = context.draft.certificateFile as File;
    // THE DOCUMENT IS CHECKED AGAINST A CAR, so there has to be one. Only the create and the policy
    // area plan this statement and both are asked for the vehicle by their own context type.
    if (context.vehicle === null) throw new Error(NO_VEHICLE_REFUSAL);
    const verdict = certificateAccepted(
        await readCertificateCoverPeriod(file),
        context.vehicle,
        context.draft.attestation,
        file,
        { startsAt: context.draft.startsAt ?? '', expiresAt: context.draft.expiresAt ?? '' },
    );
    if (verdict.refusal) throw new Error(verdict.refusal);

    const attestedBy = verdict.attested ? await attestingProfileId(client) : null;
    // POSTGRES'S OWN CLOCK, SENT AS THE VALUE: the day this record holds is the server's day, not
    // the day on the admin's laptop.
    const attestedAt = verdict.attested ? SERVER_NOW : null;

    const extension = file.name.split('.').pop();
    const path = `certificates/${crypto.randomUUID()}.${extension}`;
    const { error } = await client.storage.from(CERTIFICATE_BUCKET).upload(path, file);
    if (error) throw error;

    const url = client.storage.from(CERTIFICATE_BUCKET).getPublicUrl(path).data.publicUrl;
    return { url, path, attestedAt, attestedBy };
}

/**
 * The `insurances` columns a save writes.
 *
 * THE CERTIFICATE COLUMNS ARE NAMED ONLY WHERE THIS SAVE FILED A DOCUMENT, all three of them. An
 * ordinary edit of the insured value stages no file and must not wipe either the URL on the row or
 * the attestation standing over it — the trio belongs to the paper, not to the policy's other
 * values. And where a document IS filed, the attestation pair is written even as NULL, which is
 * what clears an earlier attestation the moment a readable certificate is filed over the scan.
 *
 * AND THE PERIOD IS NAMED ONLY WHILE THE INSTRUMENT IS UNSIGNED [F7], by the same idiom. Both
 * instruments print the cover period, so a signed one cannot have it moved underneath it — and the
 * insured value is printed by neither, which is why it is above the guard rather than behind it. The
 * screen renders the two dates as reads on a frozen year, but a screen is a courtesy: this is the
 * statement that cannot write them.
 */
function policyValues(context: WriteContext): Omit<TablesInsert<'insurances'>, 'vehicle_id'> {
    const { draft, certificate, frozen } = context;
    return {
        valued_at: draft.valuedAt,
        ...(frozen ? {} : { starts_at: draft.startsAt, expires_at: draft.expiresAt }),
        ...(certificate
            ? {
                  certificate_url: certificate.url,
                  certificate_attested_at: certificate.attestedAt,
                  certificate_attested_by: certificate.attestedBy,
              }
            : {}),
    };
}

/**
 * The `insurance_loans` columns a save writes.
 *
 * THE RATE IS THE ADMIN'S FIGURE, ZEROED WHERE THE DRIVER PAID IN FULL [D-B45]. The rate is typed
 * on the same canvas as the deposit link, so an admin who typed 40% and then linked a full-premium
 * payment would otherwise persist 40 over a year nobody was charged for. It accrues nothing either
 * way — the interest base is `amount − deposits`, which is zero — but it is PRINTED, and a stored
 * figure that contradicts the document is a figure somebody will act on.
 *
 * EMPTY IS NULL AND A TYPED ZERO IS ZERO, which `parseAdvanceNumber` has already settled upstream:
 * the sheet's `parseFloat(rate) || null` stored every typed 0% as NULL, because `0` is falsy.
 */
function loanValues(
    context: WriteContext,
    paidInFull: boolean,
): Omit<TablesInsert<'insurance_loans'>, 'insurance_contract_id'> {
    return {
        // `validateDraft` refuses every path that reaches a loan write without one.
        amount: context.draft.premiumAmount as number,
        interest_rate: paidInFull ? 0 : context.draft.interestRate,
    };
}

/**
 * Do the driver's payments cover the whole premium? — the question the stored rate turns on.
 *
 * THE COMPARISON ITSELF IS `depositsCoverPremium`'s AND IS IMPORTED, because the screen says "paid
 * in full" off that same author while this decides what goes into the column, and a second spelling
 * of one half-pesewa comparison is a second chance for a year to be stored with a rate its own
 * Schedule contradicts. What is left here is the part that is about a DRAFT rather than about two
 * figures: which of the amounts in hand is money that will actually reach the database.
 *
 * `depositAmount` OUTLIVES ITS TRANSACTION, SO THE ID IS THE GATE — the save writes an
 * `insurance_loan_deposits` row only where one is linked, so an amount with no id behind it counts
 * for nothing.
 *
 * AND ON THE SUM, WHICH IS WHERE THIS DOSSIER PARTS COMPANY WITH THE SIDE SHEET. The sheet bound one
 * deposit row and judged coverage on its amount alone; a junction may carry more — production has
 * one, loan 294 at 3000 + 977 against a premium of 3977 — and the rows this screen does not edit
 * are still money the driver paid. Judging on the edited link alone would keep a rate on a premium
 * the driver has in fact settled.
 */
function draftCoversPremium(draft: InsuranceDraft, otherDeposits: number): boolean {
    const linked = given(draft.depositTransactionId) ? (draft.depositAmount ?? 0) : 0;
    return depositsCoverPremium(draft.premiumAmount, otherDeposits + linked);
}

function duplicate(error: unknown): boolean {
    return (error as { code?: string } | null)?.code === DUPLICATE;
}

async function applyStatement(
    client: InsuranceClient,
    statement: InsuranceStatement,
    context: WriteContext,
): Promise<void> {
    const draft = context.draft;

    switch (statement.op) {
        case 'file-certificate': {
            context.certificate = await fileCertificate(client, context);
            return;
        }

        case 'insert-policy': {
            // `insurances.vehicle_id` IS NOT NULL. Only `planCreate` emits this statement, and
            // `InsuranceCreateDraft` asks for the car — this is the column saying so out loud.
            if (context.vehicleId === null) throw new Error(NO_VEHICLE_REFUSAL);
            const { data, error } = await client
                .from('insurances')
                .insert({ vehicle_id: context.vehicleId, ...policyValues(context) })
                .select('id')
                .single();
            if (error) throw error;
            context.policyId = data.id;
            return;
        }

        case 'update-policy': {
            const { error } = await client
                .from('insurances')
                .update(policyValues(context))
                .eq('id', statement.policyId);
            if (error) throw error;
            context.policyId = statement.policyId;
            return;
        }

        case 'insert-junction': {
            // NO ADOPT. `UNIQUE (insurance_id, contract_id)` refusing here would mean this contract
            // is already on this policy — which cannot be true of a policy inserted one statement
            // ago, so the refusal is a genuine surprise and is raised.
            const { data, error } = await client
                .from('insurance_contracts')
                .insert({ insurance_id: context.policyId as number, contract_id: context.contractId })
                .select('id')
                .single();
            if (error) throw error;
            context.junctionId = data.id;
            return;
        }

        case 'identity': {
            context.onIdentity?.({
                policyId: context.policyId as number,
                junctionId: context.junctionId as number,
            });
            return;
        }

        case 'insert-premium': {
            const { data, error } = await client
                .from('insurance_premiums')
                .insert({
                    insurance_id: context.policyId as number,
                    transaction_id: draft.premiumTransactionId as number,
                })
                .select('id')
                .single();
            // THE REFUSAL IS ABOUT THE TRANSACTION AND NOT ABOUT THIS POLICY. One debit records one
            // premium — `UNIQUE (transaction_id)` — so a `23505` means the payment is spoken for
            // somewhere else, and the sentence points at the picker rather than adopting a row that
            // belongs to another year.
            if (error) throw duplicate(error) ? new Error(REFUSALS.premiumTaken) : error;
            context.premiumId = data.id;
            return;
        }

        case 'update-premium': {
            const { error } = await client
                .from('insurance_premiums')
                .update({ transaction_id: draft.premiumTransactionId as number })
                .eq('id', statement.premiumId);
            if (error) throw duplicate(error) ? new Error(REFUSALS.premiumTaken) : error;
            context.premiumId = statement.premiumId;
            return;
        }

        case 'insert-discount': {
            const { error } = await client.from('insurance_premium_discounts').insert({
                premium_id: context.premiumId as number,
                transaction_id: draft.discountTransactionId as number,
            });
            if (error) throw duplicate(error) ? new Error(REFUSALS.discountTaken) : error;
            return;
        }

        case 'update-discount': {
            const { error } = await client
                .from('insurance_premium_discounts')
                .update({ transaction_id: draft.discountTransactionId as number })
                .eq('id', statement.discountId);
            if (error) throw duplicate(error) ? new Error(REFUSALS.discountTaken) : error;
            return;
        }

        case 'delete-discount': {
            const { error } = await client
                .from('insurance_premium_discounts')
                .delete()
                .eq('id', statement.discountId);
            if (error) throw error;
            return;
        }

        case 'insert-loan': {
            const values = loanValues(context, draftCoversPremium(draft, context.otherDeposits));
            const { data, error } = await client
                .from('insurance_loans')
                .insert({ insurance_contract_id: context.junctionId as number, ...values })
                .select('id')
                .single();
            if (!error) {
                context.loanId = data.id;
                return;
            }
            if (!duplicate(error)) throw error;
            // ADOPT, DO NOT DUPLICATE. `UNIQUE (insurance_contract_id)` refuses the second insert,
            // and that refusal is the news that another save already created the row this one meant
            // to create — two loans on one junction is what `weekly_rate` adds up and `total_value`
            // does not.
            const { data: adopted, error: adoptError } = await client
                .from('insurance_loans')
                .update(values)
                .eq('insurance_contract_id', context.junctionId as number)
                .select('id')
                .single();
            if (adoptError) throw adoptError;
            context.loanId = adopted.id;
            return;
        }

        case 'update-loan': {
            const { error } = await client
                .from('insurance_loans')
                .update(loanValues(context, draftCoversPremium(draft, context.otherDeposits)))
                .eq('id', statement.loanId);
            if (error) throw error;
            context.loanId = statement.loanId;
            return;
        }

        case 'insert-deposit': {
            const { error } = await client.from('insurance_loan_deposits').insert({
                loan_id: context.loanId as number,
                transaction_id: draft.depositTransactionId as number,
            });
            if (error) throw duplicate(error) ? new Error(REFUSALS.depositTaken) : error;
            return;
        }

        case 'update-deposit': {
            const { error } = await client
                .from('insurance_loan_deposits')
                .update({ transaction_id: draft.depositTransactionId as number })
                .eq('id', statement.depositId);
            if (error) throw duplicate(error) ? new Error(REFUSALS.depositTaken) : error;
            return;
        }

        case 'delete-deposit': {
            // THE LINK, NEVER THE TRANSACTION. The money stays exactly where it is in the ledger.
            const { error } = await client
                .from('insurance_loan_deposits')
                .delete()
                .eq('id', statement.depositId);
            if (error) throw error;
            return;
        }

        case 'insert-instalment': {
            const { error } = await client.from('insurance_loan_instalments').insert({
                loan_id: context.loanId as number,
                amount: draft.weeklyAmount as number,
                starts_at: draft.firstStartsAt as string,
            });
            if (!error) return;
            if (!duplicate(error)) throw error;
            // The same race one table down, refused by `UNIQUE (loan_id, starts_at)`. A loan may
            // hold several rates across its life but only one effective from any given date, and
            // the weekly-rate expression SUMS every row sharing a loan's latest start date — so a
            // duplicate here inflates the rate exactly as a duplicate loan does. The week already
            // exists: write this amount into it.
            const { error: adoptError } = await client
                .from('insurance_loan_instalments')
                .update({ amount: draft.weeklyAmount as number })
                .eq('loan_id', context.loanId as number)
                .eq('starts_at', draft.firstStartsAt as string);
            if (adoptError) throw adoptError;
            return;
        }

        case 'update-instalment': {
            const { error } = await client
                .from('insurance_loan_instalments')
                .update({
                    amount: draft.weeklyAmount as number,
                    starts_at: draft.firstStartsAt as string,
                })
                .eq('id', statement.instalmentId);
            // MOVING THE BOUND ROW ONTO A WEEK THE LOAN ALREADY HOLDS, which the insert arm above
            // adopts and this one cannot: adopting here would write this amount into the OTHER row
            // and leave the bound one where it was, so the loan would carry two weekly figures and
            // the admin would have moved neither. `UNIQUE (loan_id, starts_at)` is what keeps
            // `weekly_rate` honest — it SUMS every row sharing a loan's latest start date — so this
            // is a refusal to state, not a race to resolve.
            if (error) throw duplicate(error) ? new Error(REFUSALS.weekTaken) : error;
            return;
        }

        case 'delete-instalment': {
            const { error } = await client
                .from('insurance_loan_instalments')
                .delete()
                .eq('id', statement.instalmentId);
            if (error) throw error;
            return;
        }
    }
}

/** Remove an object this save uploaded — the only thing either rollback arm has to undo in storage. */
async function removeObject(client: InsuranceClient, context: WriteContext): Promise<void> {
    if (!context.certificate) return;
    try {
        await client.storage.from(CERTIFICATE_BUCKET).remove([context.certificate.path]);
    } catch {
        // A FAILED CLEANUP IS NOT THE FAILURE THE ADMIN IS BEING TOLD ABOUT. The object is
        // unreferenced either way; swallowing this keeps the original error on its way to the
        // screen instead of replacing it with one about a file nobody can see.
    }
    context.certificate = null;
}

/**
 * Create the policy: the document, the `insurances` row, the junction, then the money.
 *
 * THE TWO ROLLBACK ARMS ARE THE ONLY ONES, and they are the two writes whose failure leaves
 * something nothing references. A policy insert that fails leaves the uploaded object; a junction
 * insert that fails leaves the policy row AND the object — cover no contract is on, which the
 * Insurance tab would draw and nobody could reach. Everything after the junction is a row with a
 * line on the rail and a bar that saves it again.
 */
export async function createInsurance(
    client: InsuranceClient,
    draft: InsuranceCreate,
    options: { onIdentity?: (identity: InsuranceIdentity) => void } = {},
): Promise<InsuranceIdentity> {
    const refusals = validateDraft(draft, 'create', null, {
        hpClass: draft.hpClass,
        // A JUNCTION THAT DOES NOT EXIST YET CARRIES NO OTHER DEPOSITS — see the context below.
        otherDeposits: 0,
    });
    if (refusals.length > 0) throw new Error(refusals[0].message);

    const context: WriteContext = {
        contractId: draft.contractId,
        vehicleId: draft.vehicleId,
        vehicle: draft.vehicle,
        draft,
        // A JUNCTION THAT DOES NOT EXIST YET CARRIES NO OTHER DEPOSITS. Every deposit row hangs off
        // a loan this statement sequence is about to create.
        otherDeposits: 0,
        // AND IT CARRIES NO INSTRUMENT EITHER. A policy nobody has an id for has nothing signed over
        // it, so the period this sequence writes is the period the first document will print.
        frozen: false,
        policyId: null,
        junctionId: null,
        premiumId: null,
        loanId: null,
        certificate: null,
        onIdentity: options.onIdentity,
    };

    for (const statement of planCreate(draft)) {
        try {
            await applyStatement(client, statement, context);
        } catch (error) {
            if (statement.op === 'insert-policy') {
                await removeObject(client, context);
            }
            if (statement.op === 'insert-junction') {
                await client.from('insurances').delete().eq('id', context.policyId as number);
                await removeObject(client, context);
            }
            throw error;
        }
    }

    return { policyId: context.policyId as number, junctionId: context.junctionId as number };
}

/** What every area save needs of the page beyond the draft and the subject. */
export interface InsuranceSaveContext {
    contractId: number;
    /**
     * Σ of the transactions behind the deposit rows this screen does not bind [D-B45].
     *
     * THE DOSSIER EDITS THE LOWEST-ID LINK AND THE OTHERS STAY ON THE RAIL, so the money the driver
     * has paid is the sum of both — and the stored rate turns on the sum.
     *
     * REQUIRED, AND NOT DEFAULTED TO NOUGHT, BECAUSE THE DEFAULT IS THE UNSAFE DIRECTION. Nought is
     * the truth for every production loan but one, and on that one — loan 294, 3,000 already on the
     * loan against a premium of 3,977 — a caller that forgot the argument would store the typed rate
     * over a premium the driver has settled, which is a figure the generated Schedule contradicts. A
     * page that has not worked out what the other deposits are has not worked out what to write, so
     * the compiler asks rather than the writer guessing.
     */
    otherDeposits: number;
    /**
     * This year's instrument carries a signature [F7] — so `policyValues` leaves the period alone.
     *
     * REQUIRED FOR `otherDeposits`' REASON: the default is the unsafe direction. `false` is what
     * writes the two dates, and a caller that forgot the argument would move the cover period
     * printed on a page the driver has already signed.
     */
    frozen: boolean;
    /**
     * What the contract's hire purchase agreement is [D-B57] — `validateDraft`'s fourth argument.
     *
     * REQUIRED FOR THE SAME REASON AS THE TWO ABOVE: there is no safe default. `'PAPER'` would let
     * an in-app contract's financed premium be saved with no weekly recovery at all, and
     * `'NOT_PAPER'` would refuse every live year that legitimately carries none.
     */
    hpClass: HpClass;
}

/**
 * AND THE CAR, WHICH THE POLICY AREA ASKS FOR AND THE OTHER TWO DO NOT.
 *
 * `vehicleId` is read by one statement — the create's `insert-policy` — and `vehicle` by one more:
 * the certificate this area files is checked against the plate and chassis on the contract. A
 * premium or an advance save opens neither, so asking them for a car meant a contract with no
 * vehicle recorded could not have its premium payment linked, over a value its statements never
 * read. The ask is where the need is, and the compiler keeps the two apart.
 */
export type InsurancePolicySaveContext = InsuranceSaveContext & {
    vehicleId: number;
    vehicle: ContractVehicle;
};

function editContext(
    draft: InsuranceDraft,
    subject: InsurancePolicySubject,
    context: InsuranceSaveContext | InsurancePolicySaveContext,
): WriteContext {
    const car = 'vehicle' in context ? context : null;
    return {
        contractId: context.contractId,
        vehicleId: car?.vehicleId ?? null,
        vehicle: car?.vehicle ?? null,
        draft,
        otherDeposits: context.otherDeposits,
        frozen: context.frozen,
        policyId: subject.policyId,
        junctionId: subject.junctionId,
        premiumId: subject.premiumId,
        loanId: subject.loanId,
        certificate: null,
    };
}

async function runArea(
    client: InsuranceClient,
    statements: InsuranceStatement[],
    context: WriteContext,
): Promise<void> {
    for (const statement of statements) await applyStatement(client, statement, context);
}

/**
 * The policy area: the insured value, the period and the certificate.
 *
 * NOTHING HERE IS ROLLED BACK, and there is nothing that could be: an upload that fails raises
 * before the row is touched, and a row update that fails leaves the object unreferenced — which is
 * a file in a bucket, not cover nobody is on. The save is retried from the same screen with the
 * same `File` in hand.
 *
 * ON A FROZEN YEAR IT IS THE INSURED VALUE AND THE CERTIFICATE AND NOTHING ELSE [F7] — see
 * `policyValues`. The bar still stands, because neither instrument prints either of those two.
 */
export async function savePolicyArea(
    client: InsuranceClient,
    draft: InsuranceDraft,
    subject: InsurancePolicySubject,
    context: InsurancePolicySaveContext,
): Promise<void> {
    const refusals = validateDraft(draft, 'edit', subject, context).filter(
        (refusal) => refusal.area === 'policy',
    );
    if (refusals.length > 0) throw new Error(refusals[0].message);
    await runArea(client, planPolicyArea(draft, subject), editContext(draft, subject, context));
}

/** The premium area: the payment made to the insurer, and the credit that came back. */
export async function savePremiumArea(
    client: InsuranceClient,
    draft: InsuranceDraft,
    subject: InsurancePolicySubject,
    context: InsuranceSaveContext,
): Promise<void> {
    const refusals = validateDraft(draft, 'edit', subject, context).filter(
        (refusal) => refusal.area === 'premium',
    );
    if (refusals.length > 0) throw new Error(refusals[0].message);
    await runArea(client, planPremiumArea(draft, subject), editContext(draft, subject, context));
}

/**
 * The advance area: the loan, the driver's payment and the weekly recovery, in one commit.
 *
 * AN AREA SAVE ON A JUNCTION WITH NO LOAN IS ONE INSERT — 54 live junctions are that shape, and the
 * first save on one of them is what creates the loan the rest of the canvas hangs off.
 */
export async function saveAdvanceArea(
    client: InsuranceClient,
    draft: InsuranceDraft,
    subject: InsurancePolicySubject,
    context: InsuranceSaveContext,
): Promise<void> {
    const refusals = validateDraft(draft, 'edit', subject, context).filter(
        (refusal) => refusal.area === 'advance',
    );
    if (refusals.length > 0) throw new Error(refusals[0].message);
    await runArea(client, planAdvanceArea(draft, subject), editContext(draft, subject, context));
}

/**
 * Delete the policy and everything hanging off it — THE ONE RPC THIS DOSSIER CALLS.
 *
 * IT IS THE SIDE SHEET'S OWN DOOR, unchanged: `delete_insurance_cascade` takes the POLICY id, refuses a
 * policy another contract is also on, and takes the junction's loans, their deposits, their
 * instalments, the real bank `transactions` behind them and the `insurance_loan_agreements` rows any
 * in-app signed document cascades from. The server's refusal is printed verbatim by the caller,
 * because a shared policy is a fact about the estate that no sentence written here could improve.
 */
export async function deletePolicy(client: InsuranceClient, policyId: number): Promise<void> {
    const { error } = await client.rpc('delete_insurance_cascade', { p_insurance_id: policyId });
    if (error) throw error;
}

// ─── What a save moves, and therefore what has to be refetched ───────────────

/**
 * THE SIDE SHEET'S TWO LISTS, UNIONED, PLUS WHAT THIS DOSSIER ADDS.
 *
 * The side sheet invalidated one set on save and a larger set on delete; a dossier that saves and
 * deletes from the same page has no reason to keep them apart, and the delete's extra key
 * (`contract-repayments`) is moved by an ordinary save here too — linking a deposit changes what the
 * repayments table shows.
 *
 * `String(contractId)` IS NOT A TYPO. The insights query is keyed `['contract-insights', contractId]`
 * where `contractId` is the ROUTE PARAM — a string — so a number here would match nothing and
 * refetch nothing.
 *
 * `advanceForwardKey` IS HERE BECAUSE THE ADVANCE DOSSIER ADDS THIS YEAR'S RECOVERY UP. Its "what it
 * adds up to" panel sums every insurance loan instalment on the contract beside its own, so a weekly
 * recovery written here moves a figure on a page this one does not own.
 *
 * AND THE CHAIN KEY IS NAMED THOUGH THE FIRST ENTRY ALREADY REACHES IT. `['contract-insurance']` is
 * a prefix of both this dossier's reads, so the policy read and the chain count are both refetched
 * by it today; naming the chain is what keeps that true if the prefix is ever narrowed to one
 * contract's entry.
 */
export function insuranceInvalidationKeys(contractId: number): readonly unknown[][] {
    return [
        [...CONTRACT_INSURANCE_KEY],
        ['unassigned-transactions'],
        ['prep-status'],
        ['contract-repayments'],
        [...AGREEMENT_BOARD_SUBJECTS_KEY],
        [...DEPOSIT_LEDGER_KEY],
        [...PREMIUM_YEARS_KEY],
        ['contract-insights', String(contractId)],
        [...advanceForwardKey(contractId)],
        [...contractPolicyChainKey(contractId)],
    ];
}

// ─── Which field a refusal is about ──────────────────────────────────────────

/**
 * ONE MAP PER WRITE, NOT ONE MAP FOR THE PAGE, and the collisions are why: "That payment is already
 * recorded against another policy." is raised by the premium write and by the deposit write, and the
 * sentence alone does not name a field. The sentence plus the area does.
 *
 * THE MATCH IS EXACT, which is what keeps it honest: a PostgREST constraint sentence, a network
 * failure or a message somebody edits without editing this file all miss, come back unattributed and
 * land on the area's own line — which is where an unattributable refusal belongs.
 */
export const POLICY_AREA_REFUSALS: RefusalFieldMap = {
    [REFUSALS.valuedAt]: 'valuedAt',
    [REFUSALS.valuedAtCleared]: 'valuedAt',
    [REFUSALS.startsAt]: 'startsAt',
    [REFUSALS.expiresAt]: 'expiresAt',
    [REFUSALS.periodOrder]: 'expiresAt',
};

/**
 * THE CERTIFICATE REFUSALS — all seven, and they are PREFIXES because two of them name their own
 * values: the mismatch sentence prints the identifiers that disagree, and the vehicle-unrecorded one
 * names whichever identifier is missing. Prefixes are tried only after every exact key has missed,
 * so one can never shadow a constant.
 *
 * ALL SEVEN LAND ON THE UPLOAD ROW, INCLUDING THE ONE ABOUT THE VEHICLE: the document is what is
 * wrong with the save in every case, and taking it back out is what lets the rest of the area save.
 */
export const POLICY_AREA_REFUSAL_PREFIXES: RefusalPrefixMap = {
    [VEHICLE_REFUSAL_PREFIX]: 'certificateFile',
    [UNREADABLE_REFUSAL_PREFIX]: 'certificateFile',
    [NOT_PDF_REFUSAL_PREFIX]: 'certificateFile',
    [TOO_LARGE_REFUSAL_PREFIX]: 'certificateFile',
    [NOT_STATUTORY_REFUSAL_PREFIX]: 'certificateFile',
    [NO_IDENTIFIER_REFUSAL_PREFIX]: 'certificateFile',
    [VEHICLE_UNRECORDED_REFUSAL_PREFIX]: 'certificateFile',
};

export const PREMIUM_AREA_REFUSALS: RefusalFieldMap = {
    [REFUSALS.premium]: 'premiumTransactionId',
    [REFUSALS.premiumCleared]: 'premiumTransactionId',
    [REFUSALS.premiumTaken]: 'premiumTransactionId',
    [REFUSALS.discountTaken]: 'discountTransactionId',
    // THE SENTENCE IS ABOUT THE PREMIUM AND THE REFUSAL BELONGS TO THE DISCOUNT, which is the link
    // the admin has to take back out before this area can save.
    [REFUSALS.discountOrphan]: 'discountTransactionId',
};

export const ADVANCE_AREA_REFUSALS: RefusalFieldMap = {
    [REFUSALS.amount]: 'premiumAmount',
    [REFUSALS.amountCleared]: 'premiumAmount',
    [REFUSALS.positive]: 'weeklyAmount',
    [REFUSALS.half]: 'weeklyAmount',
    // [D-B57] — the two halves of the pair, each on its own box, and the refusal to clear a live
    // recovery on the box that holds the figure being emptied.
    [REFUSALS.weeklyRequired]: 'weeklyAmount',
    [REFUSALS.firstRequired]: 'firstStartsAt',
    [REFUSALS.weeklyCleared]: 'weeklyAmount',
    // The deposit's own `UNIQUE (transaction_id)`. The sentence is the premium's word for word, and
    // it belongs to a different picker — which is why these are three maps and not one.
    [REFUSALS.depositTaken]: 'depositTransactionId',
    // `UNIQUE (loan_id, starts_at)` refusing a MOVE, so the box to change is the day and not the
    // amount: the week the admin is moving on to is the one already spoken for.
    [REFUSALS.weekTaken]: 'firstStartsAt',
};

/** The `fieldErrorsFor` each area's bar is wired with. */
export const insuranceRefusalMaps: Record<
    InsuranceArea,
    { map: RefusalFieldMap; prefixes?: RefusalPrefixMap }
> = {
    policy: { map: POLICY_AREA_REFUSALS, prefixes: POLICY_AREA_REFUSAL_PREFIXES },
    premium: { map: PREMIUM_AREA_REFUSALS },
    advance: { map: ADVANCE_AREA_REFUSALS },
    agreement: { map: {} },
};

/** The field-scoped refusal for one raised message on one area, or undefined where nothing claims it. */
export function attributeInsuranceRefusal(
    area: InsuranceArea,
    message: string | null | undefined,
): Record<string, string> | undefined {
    const { map, prefixes } = insuranceRefusalMaps[area];
    return attributeRefusal(map, message, prefixes);
}
